import {
    Force_APIListing,
    Force_APITestDrive,
    Force_CopilotListing,
    Force_PackageListing,
    Force_PackageTestDrive,
    schemas,
} from '@oneappexchange/oaf-schema';
import { validate as isUUID } from 'uuid';
import { TryOptionType, TryOptionTypes } from '~/modules/appx_site_schema';
import { FeatureFlags } from '~/modules/feature_flags';
import { Routes } from '~/modules/routes';
import { logger } from '~/modules/utilities/cross_env_utils';
import {
    isPluginListingId,
    isSkillListingId,
    PLUGIN_LISTING_ID_PATTERN,
    SKILL_LISTING_ID_PATTERN,
} from '~/modules/utilities/listingIdPatterns';
import type { ListingPlatformType } from '~/modules/utilities/listingPlatformType';
import { isPaidWithCheckout } from '~/modules/utilities/pricing_utils';
import { ListingResponse } from '../services/listing_service';
import { AI_PLUGIN_EXTENSION_TYPE, AI_SKILL_EXTENSION_TYPE } from './aiListingExtensionTypes';

// Re-exported for backward compatibility: callers previously imported these from this module
// before the patterns/detectors moved to the leaf module `listingIdPatterns.ts` (needed so
// `listingCard.ts` could import the pattern without pulling this module's heavier import chain
// into the card bundle).
export { isPluginListingId, isSkillListingId, PLUGIN_LISTING_ID_PATTERN, SKILL_LISTING_ID_PATTERN };
// The platform-badge union now lives in the leaf module `listingPlatformType.ts` so the card
// layer can share it without importing this heavier module; re-exported here for callers that
// already import it from `listing_utils`. Type-only re-export: `ListingPlatformType` has no
// runtime value, so a plain `export { ... }` would emit an invalid runtime re-export that
// Babel can't erase (build_client fails with "module has no exports"). (W-24063286)
export type { ListingPlatformType };

const EXTENSION_TYPE_PACKAGE_LISTING = 'listing/extensions/force/listings/Listing/extensions/PackageListing';
const EXTENSION_TYPE_COPILOT_LISTING = 'listing/extensions/force/listings/Listing/extensions/CopilotListing';
const EXTENSION_TYPE_API_LISTING = 'listing/extensions/force/listings/Listing/extensions/APIListing';
const EXTENSION_TYPE_TEST_DRIVE = 'listing/extensions/force/listings/Listing/extensions/PackageListing/TestDrive';
const EXTENSION_TYPE_API = 'listing/extensions/force/listings/Listing/extensions/APIListing/TestDrive';
const SALESFORCE_LISTING_ID_PATTERN = /^[A-Za-z0-9]{15}(?:[A-Za-z0-9]{3})?$/;

// The listingId that we store in the userAccount comes directly from the url.
// As a result we dont know which id it corresponds to, so we must check all the Ids.
export function isListingMatchingId(listing: ListingResponse, listingId: string) {
    return (
        listing.appExchangeId === listingId ||
        listing.oafId === listingId ||
        listing.tzId === listingId ||
        listing.external_id === listingId
    );
}

export function getListingId(listing: ListingResponse) {
    return listing?.appExchangeId || listing?.oafId || listing?.tzId || listing?.external_id || '';
}

export const TECHNOLOGY_EXTENSION_MAP: Record<
    schema.oaf.BaseListing['technology'],
    schema.oaf.ListingExtension['extensionType']
> = {
    'force/bolt': 'listing/extensions/force/listings/Listing',
    'force/copilot': 'listing/extensions/force/listings/CopilotListing',
    'force/flow': 'listing/extensions/force/listings/Listing',
    'force/lightning': 'listing/extensions/force/listings/Listing',
    'force/package': 'listing/extensions/force/listings/Listing',
    'salesforce-platform/api': 'listing/extensions/force/listings/Listing',
    'quip/package': 'listing/extensions/quip/listings/Listing',
    'b2c-commerce/package': 'listing/extensions/b2c/listings/Listing',
    'b2c-commerce/api': 'listing/extensions/b2c/listings/Listing',
    'marketing-cloud/api': 'listing/extensions/marketing-cloud/listings/Listing',
    slack: 'listing/extensions/slack/listings/Listing',
    'tableau/accelerator': 'listing/extensions/tableau/listings/Listing',
    consultant: 'listing/extensions/consultant/listings/Listing',
    'ai-skill': AI_SKILL_EXTENSION_TYPE,
    'ai-plugin': AI_PLUGIN_EXTENSION_TYPE,
};

export function isConsultantListing(listing?: ListingResponse): boolean {
    return listing?.technology === 'consultant';
}

export function isSlackListing(listing?: ListingResponse): boolean {
    return listing?.technology === 'slack';
}

export function isSkillListing(listing?: ListingResponse): boolean {
    return listing?.technology === 'ai-skill';
}

export function isPluginListing(listing?: ListingResponse): boolean {
    return listing?.technology === 'ai-plugin';
}

export function isExternalAgentListing(listing?: ListingResponse): boolean {
    return isSkillListing(listing) || isPluginListing(listing);
}

/**
 * Maps a listing to the platform badge icon rendered by the `with-badge` listing card variant.
 * The badge reflects the listing's platform *identity*, derived straight from its technology:
 * consultant listings get the consultants icon, slack listings get the Slack icon, and every
 * other technology falls back to Salesforce. Identity is intentionally independent of the Slack
 * pilot flag and of whether a marketplace slug resolved — the badge stays truthful while the flag
 * decides separately whether the card is shown or may link out to the Slack marketplace (see
 * `isSlackMarketplaceListing` in `listingCard.ts`). (W-24063286)
 */
export function getListingPlatformType(listing?: ListingResponse): ListingPlatformType {
    if (isConsultantListing(listing)) {
        return 'consultants';
    }
    return listing?.technology === 'slack' ? 'slack' : 'salesforce';
}

/**
 * The Slack marketplace slug for a slack listing — the value the card links to at
 * `${SLACK_MARKETPLACE_URL}/<slug>`. This is the Slack extension's own `slug` field (e.g.
 * `A019L0MA2CV-bt-cloud-work`, an app id plus slugified name), NOT the base listing's
 * `external_id` (the listing-stream identifier, e.g. `A0JNRNYAJ`) — the two are distinct fields in
 * the OAF schema and generally differ. `selectExtensionListing` returns an empty schema instance
 * on a miss, so a falsy `slug` collapses to undefined. Returns undefined for non-slack listings.
 * (W-24063286)
 */
export function getSlackSlug(listing?: ListingResponse): string | undefined {
    return listing?.technology === 'slack'
        ? selectExtensionListing<schema.oaf.Slack_Listing>(listing).slug || undefined
        : undefined;
}

export function isSupportedTechnologyType(listing?: ListingResponse) {
    return !!(listing && TECHNOLOGY_EXTENSION_MAP[listing.technology]);
}

export function selectExtensionListing<T = schema.oaf.Force_Listing>(listing: ListingResponse) {
    if (!listing?.technology) {
        return schemas['listing/extensions/force/listings/Listing'].new() as unknown as T;
    }

    const extensionType = TECHNOLOGY_EXTENSION_MAP[listing.technology];
    if (!extensionType) {
        logger.warn(
            `Failed to find extensionType for listing with technology '${listing.technology}' we might be missing an entry in TECHNOLOGY_EXTENSION_MAP`,
        );
        return schemas['listing/extensions/force/listings/Listing'].new() as unknown as T;
    }

    const extension = listing.extensions?.find((test) => test.extensionType === extensionType)?.data as unknown as T;
    if (!extension) {
        logger.warn(
            `Failed to find listing extension '${extensionType}' for listing: external_id=${listing.external_id}, technology=${listing.technology}`,
        );
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return schemas[extensionType].new() as unknown as T;
    }
    return extension;
}

/**
 * The listing's solution package version as "v{major}.{minor}.{patch}" (e.g. "v3.4.2"), or ''
 * when there is none. Shared by the Metadata card and the Security tab so they stay consistent.
 */
export function getCurrentVersion(listing?: ListingResponse): string {
    const solution = listing?.solution?.solution as schema.oaf.Force_Solution_Package | undefined;
    const version = solution?.version;
    if (!version) {
        return '';
    }
    const major = version.majorVersion ?? 0;
    const minor = version.minorVersion ?? 0;
    const patch = version.patchVersion ?? 0;
    return `v${major}.${minor}.${patch}`;
}

export function isValidListingId(listingId?: string): listingId is string {
    // Deny falsy values (null, undefined, empty strings)
    if (!listingId) {
        return false;
    }

    // Allow skill and plugin external ids, which are neither UUIDs nor SFIDs.
    if (isSkillListingId(listingId) || isPluginListingId(listingId)) {
        return true;
    }

    const numChars = listingId.length;

    // Allow UUIDs
    if (numChars === 36) {
        return isUUID(listingId);
    }

    // Allow SFIDs. Validate their alphanumeric shape as well as their length so a 15- or
    // 18-character legacy external ID containing a hyphen cannot pass through this fallback.
    return SALESFORCE_LISTING_ID_PATTERN.test(listingId);
}

export function isListingReviewed(listing: ListingResponse | null): boolean {
    if (!listing) {
        return false;
    }

    const approvalDate = listing.solution?.securityReview?.approvalDate;
    return !!approvalDate;
}

// `pathPrefix` (e.g. `/jp`) is inserted between `baseUrl` and the route name so callers can preserve
// the current locale prefix without pre-concatenating.
export function generateListingUrl(
    baseUrl: string,
    listing?: ListingResponse | null,
    params?: string,
    pathPrefix = '',
) {
    const route = listing?.technology === 'consultant' ? Routes.CONSULTANT_LISTING : Routes.LISTING;
    return `${baseUrl}${pathPrefix}/${route}${params ? `?${params}` : ''}`;
}

export function selectListingTryOptions(listing: ListingResponse): Array<TryOptionType> {
    const result: Array<TryOptionType> = [];

    if (isPackageSolutionListing(listing)) {
        result.push(TryOptionTypes.Sandbox);
    }
    if (isTestDriveListing(listing)) {
        result.push(TryOptionTypes.TestDrive);
    }
    if (isTrialForceListing(listing)) {
        result.push(TryOptionTypes.Trialforce);
    }

    return result;
}

export function isTestDriveListing(listing: ListingResponse) {
    const copilotTestDrive = forceCopilotTestDrive(listing);
    return (
        forceTestDrive(listing)?.enableTestDrive ||
        forceAPITestDrive(listing)?.enableTestDrive ||
        (copilotTestDrive?.enableTestDrive && hasTestDriveOrgId(copilotTestDrive))
    );
}

// Finds `extension.data` for the extension whose `extensionType` matches, at whatever nesting
// level `container` sits at — the base listing's own `extensions` array and every nested
// extension's `extensions` array share this same `{ extensions: [{ extensionType, data }] }`
// shape, just with a different (mutually incompatible) `extensionType` literal union per level.
function findExtensionData<T>(
    container: { extensions?: Array<{ extensionType: string; data: unknown }> } | undefined,
    extensionType: string,
): T | undefined {
    return container?.extensions?.find((extension) => extension.extensionType === extensionType)?.data as T;
}

function forceTestDrive(listing: ListingResponse): Force_PackageTestDrive | undefined {
    return findExtensionData<Force_PackageTestDrive>(forcePackageListingExtensions(listing), EXTENSION_TYPE_TEST_DRIVE);
}

function forceAPITestDrive(listing: ListingResponse): Force_APITestDrive | undefined {
    return findExtensionData<Force_APITestDrive>(forceAPIListingExtensions(listing), EXTENSION_TYPE_API);
}

// publishing-service nests copilot test drives under `CopilotListing` using the same
// `PackageListing/TestDrive` extension shape as `force/package`, but the published
// `Force_CopilotListing` type doesn't declare an `extensions` field for that nesting (see "Known
// nuance" in docs/W-23060892-copilot-package-and-test-drive-support.md) — hence the extra cast.
function forceCopilotTestDrive(listing: ListingResponse): Force_PackageTestDrive | undefined {
    return findExtensionData<Force_PackageTestDrive>(
        forceCopilotListingExtensions(listing) as unknown as Force_PackageListing,
        EXTENSION_TYPE_TEST_DRIVE,
    );
}

function hasTestDriveOrgId(testDrive: Force_PackageTestDrive | undefined): boolean {
    return Boolean(testDrive?.testDriveOrgID);
}

export function hasCopilotTestDriveCredentials(listing: ListingResponse): boolean {
    return hasTestDriveOrgId(forceCopilotTestDrive(listing));
}

export function forcePackageListingExtensions(listing: ListingResponse): Force_PackageListing | undefined {
    return findExtensionData<Force_PackageListing>(selectExtensionListing(listing), EXTENSION_TYPE_PACKAGE_LISTING);
}

export function forceAPIListingExtensions(listing: ListingResponse): Force_APIListing | undefined {
    return findExtensionData<Force_APIListing>(selectExtensionListing(listing), EXTENSION_TYPE_API_LISTING);
}

export function forceCopilotListingExtensions(listing: ListingResponse): Force_CopilotListing | undefined {
    return findExtensionData<Force_CopilotListing>(selectExtensionListing(listing), EXTENSION_TYPE_COPILOT_LISTING);
}

export function isPackageSolutionListing(listing: ListingResponse): boolean {
    return (
        forcePackageListingExtensions(listing)?.provisionFlow === 'Package' ||
        forceCopilotListingExtensions(listing)?.provisionFlow === 'Package'
    );
}

export function isTrialForceListing(listing: { plugins: { pluginType: string }[] }): boolean {
    return (
        listing?.plugins?.some(
            (plugin: { pluginType: string }) => plugin.pluginType === 'listing/plugins/TrialTemplate',
        ) || false
    );
}

export function isCopilotListing(listing?: ListingResponse) {
    return listing?.technology === 'force/copilot';
}

export function hasAgenticDetails(listing: ListingResponse): boolean {
    if (!isCopilotListing(listing)) {
        return false;
    }
    const extensionListing = selectExtensionListing<schema.oaf.Force_Copilot_Listing>(listing);
    if (!extensionListing) {
        return false;
    }
    return Boolean(extensionListing.numAgents || extensionListing.numTopics || extensionListing.numActions);
}

export function hasMCPServers(listing: ListingResponse): boolean {
    if (!isCopilotListing(listing)) {
        return false;
    }
    const extensionListing = selectExtensionListing<schema.oaf.Force_Copilot_Listing>(listing);
    if (!extensionListing) {
        return false;
    }
    return typeof extensionListing.numMCPServers === 'number' && extensionListing.numMCPServers > 0;
}

/**
 * Returns true if the listing is W9 compliant (US publisher, paid with Salesforce checkout → requires W9Compliance true).
 */
export function isListingW9Compliant(listing: ListingResponse): boolean {
    // W9 check not required if feature flag is disabled
    if (!FeatureFlags.FF_PARTNER_W9_COLLECTION) {
        return true;
    }

    const publisherExt = listing?.publisher?.extensions?.[0] as schema.oaf.PublisherExtension | undefined;
    const data = publisherExt?.data;
    const checkoutCountry = data?.checkoutCountry;

    // W9 check not required for non-US publishers
    if (checkoutCountry !== 'US') {
        return true;
    }

    // For US publishers, check W9 compliance only for paid-with-checkout listings
    const pricing = listing?.pricing as schema.oaf.Pricing | undefined;
    // Non checkout listings don't require W9 compliance
    if (!isPaidWithCheckout(pricing)) {
        return true;
    }
    return data?.W9Compliance ?? false;
}

/**
 * Returns true if the trust center should be displayed for the given listing and publisher.
 * Trust center is shown when:
 * 1. The publisher has a trust center URL
 * 2. The listing exists and is not a consultant listing
 */
export function shouldShowTrustCenter(
    listing: ListingResponse | undefined,
    trustCenterUrl: string | undefined,
): boolean {
    return !!trustCenterUrl && !!listing && !isConsultantListing(listing);
}
